import { LoadingFallback } from "@/components/loading-fallback";
import { useMultipleLiteDashboardStatistics } from "@/hooks/tiktokOrder.hooks";
import { UserStateContext } from "@/contexts/UserStateProvider";
import { useContext, useEffect, useState } from "react";
import {
  DollarSign,
  TrendingUp,
  TrendingDown,
  Info,
  ChevronDown,
  Settings2Icon,
  ChevronRight,
  Loader2,
  Check,
} from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { useOrganisationPlan } from "@/hooks/organisation.hooks";
import { TaxSettingsPopover } from "@/components/tax-settings-popover";
import { PlatformFeesConfigureButton } from "@/components/platformCommissionFees/platform-fees-configure-button";
import { FbtFulfillmentFeesConfigureButton } from "@/components/fbtFulfillmentFees/fbt-fulfillment-fees-configure-button";
import { _Button } from "@/components/button";
import { useNavigate } from "react-router-dom";
import { Currency } from "@/types/Currency";
import { CogsCalcPopover } from "@/components/cogs-calc-popover";
import {
  Select,
  SelectContent,
  SelectValue,
  SelectTrigger,
  SelectItem,
} from "@/components/ui/select";
import { DateRangeGroup } from "@/types/DateRangeGroup";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { SelectComparisonDateRange } from "@/components/select-comparison-date-range";
import moment from "moment";
import { Button } from "@/components/ui/button";

const formatValue = (value: number | undefined | null, currency: Currency) => {
  if (!value) return `${currency.symbol}0.00`;
  return `${currency.symbol}${value.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const RenderMetric = ({
  label,
  subtitle,
  value,
  tooltip,
  isNegative = false,
  isAdded = false,
  isSmall = false,
  isPlainFormat = false,
  isLowOpacity = false,
  isPercentage = false,
  extra,
  children,
  className,
  overrideRender,
}: {
  label: string;
  value: number | undefined | null;
  tooltip: string;
  isNegative?: boolean;
  isAdded?: boolean;
  isSmall?: boolean;
  subtitle?: string;
  isPlainFormat?: boolean;
  isLowOpacity?: boolean;
  isPercentage?: boolean;
  extra?: React.ReactNode;
  children?: React.ReactNode;
  className?: string;
  overrideRender?: React.ReactNode;
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const hasChildren = !!children;
  const { currency } = useContext(UserStateContext);

  return (
    <>
      <div
        className={cn(
          "flex flex-wrap justify-between items-center text-sm py-1.5 border-b px-4",
          hasChildren && "cursor-pointer hover:bg-muted/20",
          className
        )}
        onClick={() => hasChildren && setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-2">
          <TooltipProvider delayDuration={0}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Info
                  size={12}
                  className="text-muted-foreground opacity-50 hover:opacity-100 transition-opacity cursor-help"
                />
              </TooltipTrigger>
              <TooltipContent className={"w-[200px]"}>
                <p>{tooltip}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>

          <div>
            <div
              className={cn(
                "text-muted-foreground",
                isLowOpacity ? "opacity-70" : ""
              )}
            >
              {label}
            </div>
            {subtitle && (
              <div className="text-muted-foreground text-xs opacity-70">
                {subtitle}
              </div>
            )}
          </div>

          {extra && <div onClick={(e) => e.stopPropagation()}>{extra}</div>}
          {hasChildren && (
            <ChevronRight
              className={cn(
                "h-4 w-4 transition-transform",
                isExpanded && "transform rotate-90"
              )}
            />
          )}
        </div>
        {!overrideRender ? (
          <span
            className={cn(
              "font-medium",
              isLowOpacity ? "opacity-70" : "",
              isNegative
                ? "text-red-500"
                : isAdded
                ? "text-green-500"
                : "text-foreground",
              isSmall ? "text-xs" : "text-sm"
            )}
          >
            {isNegative ? "-" : isAdded ? "+" : ""}{" "}
            {isPlainFormat
              ? value
                ? value
                : "-"
              : isPercentage
              ? `${
                  value
                    ? value.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })
                    : "0"
                }%`
              : formatValue(value, currency)}
          </span>
        ) : (
          overrideRender
        )}
      </div>
      {hasChildren && isExpanded && (
        <div className="opacity-100">{children}</div>
      )}
    </>
  );
};

const RenderTile = ({
  current,
  previous,
  dateRange,
  showComparison = true,
  dateRangeOptions,
  getIconForDateRange,
  adSpendEnabled,
  adSpendSyncStatus,
  customDateRange,
}: {
  current: any | null;
  previous: any | null;
  customDateRange?: {
    from: Date | undefined;
    to: Date | undefined;
  };
  dateRange: string;
  showComparison: boolean;
  dateRangeOptions: { value: string; label: string }[];
  getIconForDateRange: (dateRange: string) => React.ReactNode;
  adSpendEnabled: boolean;
  adSpendSyncStatus?: "PENDING" | "COMPLETED";
}) => {
  const renderCustomDateRange = () => {
    if (!customDateRange?.from || !customDateRange?.to) {
      return null;
    }

    return (
      <div className="flex flex-col">
        <div className="text-sm">
          <span className="text-muted-foreground">From:</span> {moment(customDateRange.from).format("MMM Do YYYY")}
        </div>
        <div className="text-sm">
          <span className="text-muted-foreground">To:</span> {moment(customDateRange.to).format("MMM Do YYYY")}
        </div>
      </div>
    );
  };
  const calculatePercentageChange = (current: number, previous: number) => {
    if (!previous) return 0;
    return ((current - previous) / previous) * 100;
  };

  const renderTrend = (percentage: number) => {
    if (percentage > 0) {
      return (
        <div className="flex items-center text-green-500 text-sm">
          <TrendingUp size={14} className="mr-1" />
          <span>+{percentage.toFixed(1)}%</span>
        </div>
      );
    } else if (percentage < 0) {
      return (
        <div className="flex items-center text-red-500 text-sm">
          <TrendingDown size={14} className="mr-1" />
          <span>{percentage.toFixed(1)}%</span>
        </div>
      );
    }
    return null;
  };

  return (
    <Collapsible className="h-full rounded-lg border bg-card text-card-foreground shadow-sm flex flex-col justify-between">
      <div>
        <div className="pt-6 px-6 flex flex-wrap justify-between items-start">
          <div className="w-full space-y-1 flex flex-wrap justify-between items-center">
            <div className="xl:w-2/3 w-full flex items-center gap-4">
              {getIconForDateRange(dateRange)}
              {dateRange.includes("custom")
                ? renderCustomDateRange()
                : dateRangeOptions.find((opt) => opt.value === dateRange)?.label}
            </div>
            <div className="xl:w-1/3 w-full flex items-center justify-end gap-4">
              {showComparison &&
                renderTrend(
                  calculatePercentageChange(
                    current?.totalGrossSales ?? 0,
                    previous?.totalGrossSales ?? 0
                  )
                )}
            </div>
          </div>
        </div>

        <div className="mt-4 border-t flex flex-col justify-between">
          <RenderMetric
            label="Net Units Sold"
            value={current?.units - current?.cancelledUnits}
            tooltip="The total number of units sold. Excludes cancelled units."
            isPlainFormat={true}
          >
            <RenderMetric
              className="pl-8"
              isLowOpacity={true}
              label="Units"
              value={current?.units}
              tooltip="The total number of units sold. Includes cancelled units."
              isPlainFormat={true}
              isAdded={true}
            />
            <RenderMetric
              className="pl-8"
              label="Cancelled"
              value={current?.cancelledUnits}
              tooltip="Total cancelled units."
              isPlainFormat={true}
              isNegative={true}
              isLowOpacity={true}
              children={
                <>
                  <RenderMetric
                    className="pl-16"
                    label="Cancelled (by TikTok)"
                    value={current?.systemCancelledUnits}
                    tooltip="Units cancelled by the system (TikTok). These are usually due to: failed / missed payment, failed delivery, or other issues."
                    isPlainFormat={true}
                    isLowOpacity={true}
                  />
                  <RenderMetric
                    className="pl-16"
                    label="Cancelled (by Seller)"
                    value={current?.sellerCancelledUnits}
                    tooltip="Units cancelled by the seller (you)."
                    isPlainFormat={true}
                    isLowOpacity={true}
                  />
                  <RenderMetric
                    className="pl-16"
                    label="Cancelled (by Buyer)"
                    value={current?.buyerCancelledUnits}
                    tooltip="Units cancelled by the buyer."
                    isPlainFormat={true}
                    isLowOpacity={true}
                  />
                </>
              }
            />
          </RenderMetric>
          {/* <RenderMetric
            label="Gross Sales"
            value={current?.totalGrossSales}
            tooltip="Gross revenue amount before seller discounts, shipping and platform discounts are deducted."
            children={
              <>
                <RenderMetric
                  className="pl-8"
                  label="Customer Payment"
                  value={
                    current?.totalCustomerPayment -
                    current?.totalCancelledCustomerPayment -
                    (current?.totalShippingSalePrice -
                      current?.totalCancelledShippingSalePrice)
                  }
                  tooltip="Customer payment after deducting shipping fees paid by the buyer. Excludes cancelled payments."
                  isAdded={true}
                  isSmall={true}
                  isLowOpacity={true}
                  children={
                    <>
                      <RenderMetric
                        className="pl-16"
                        label="Payment"
                        value={
                          Math.abs(current?.totalCustomerPayment ?? 0) -
                          Math.abs(current?.totalCancelledCustomerPayment ?? 0)
                        }
                        tooltip="Total payments received from customers. Excludes cancelled payments."
                        isNegative={false}
                        isAdded={true}
                        isSmall={true}
                        isLowOpacity={true}
                        children={
                          <>
                            <RenderMetric
                              className="pl-20"
                              label="Total"
                              value={current?.totalCustomerPayment}
                              tooltip="Total payments received from customers. Includes cancelled payments."
                              isAdded={true}
                              isSmall={true}
                              isLowOpacity={true}
                            />
                            <RenderMetric
                              className="pl-20"
                              label="Cancelled"
                              value={current?.totalCancelledCustomerPayment}
                              tooltip="Total cancelled payments received from customers."
                              isNegative={true}
                              isAdded={true}
                              isSmall={true}
                              isLowOpacity={true}
                            />
                          </>
                        }
                      />
                      <RenderMetric
                        className="pl-16"
                        label="Buyer Shipping Fees"
                        value={
                          current?.totalShippingSalePrice -
                          current?.totalCancelledShippingSalePrice
                        }
                        tooltip="Shipping fees paid by the buyer. Deducted from the customer payment as it does not count towards the seller's revenue."
                        isNegative={true}
                        isSmall={true}
                        isLowOpacity={true}
                      />
                    </>
                  }
                />
                <RenderMetric
                  className="pl-8"
                  label="Platform Discounts"
                  value={current?.totalNetPlatformDiscounts}
                  tooltip="Platform discounts applied to the customer payment. Excludes cancelled platform discounts."
                  isAdded={true}
                  isSmall={true}
                  isLowOpacity={true}
                  children={
                    <>
                      <RenderMetric
                        className="pl-16"
                        label="Total"
                        value={current?.totalPlatformDiscounts}
                        tooltip="Total platform discounts applied to the customer payment. Includes cancelled platform discounts."
                        isAdded={true}
                        isSmall={true}
                        isLowOpacity={true}
                      />
                      <RenderMetric
                        className="pl-16"
                        label="Cancelled"
                        value={current?.totalCancelledPlatformDiscounts}
                        tooltip="Total cancelled platform discounts applied to the customer payment."
                        isNegative={true}
                        isAdded={true}
                        isSmall={true}
                        isLowOpacity={true}
                      />
                    </>
                  }
                />
                <RenderMetric
                  className="pl-8"
                  label="Seller Discounts"
                  value={current?.totalNetSellerDiscounts}
                  tooltip="Seller discounts applied to the customer payment. Excludes cancelled seller discounts."
                  isNegative={false}
                  isAdded={true}
                  isSmall={true}
                  isLowOpacity={true}
                  children={
                    <>
                      <RenderMetric
                        className="pl-16"
                        label="Total"
                        value={current?.totalSellerDiscounts}
                        tooltip="Total seller discounts applied to the customer payment. Includes cancelled seller discounts."
                        isAdded={true}
                        isSmall={true}
                        isLowOpacity={true}
                      />
                      <RenderMetric
                        className="pl-16"
                        label="Cancelled"
                        value={current?.totalCancelledSellerDiscounts}
                        tooltip="Total cancelled seller discounts applied to the customer payment."
                        isNegative={true}
                        isAdded={true}
                        isSmall={true}
                        isLowOpacity={true}
                      />
                    </>
                  }
                />
              </>
            }
          /> */}
          <RenderMetric
            label="GMV"
            subtitle="incl. cancelled orders"
            value={
              current?.totalCustomerPayment -
              current?.totalShippingSalePrice +
              current?.totalPlatformDiscounts +
              current?.totalSellerShippingSalePrice
            }
            tooltip="Net revenue amount after seller discounts are deducted. This figure does not include shipping fees or cancelled orders."
            children={
              <>
                <RenderMetric
                  className="pl-8"
                  label="Payment"
                  subtitle="minus buyer shipping fees"
                  value={
                    current?.totalCustomerPayment -
                    current?.totalShippingSalePrice +
                    current?.totalSellerShippingSalePrice
                  }
                  tooltip="Customer payment after deducting shipping fees paid by the buyer. Excludes cancelled payments."
                  isAdded={true}
                  isSmall={true}
                  isLowOpacity={true}
                  children={
                    <>
                      <RenderMetric
                        className="pl-16"
                        label="Payment"
                        value={Math.abs(current?.totalCustomerPayment ?? 0)}
                        tooltip="Total payments received from customers. Excludes cancelled payments."
                        isNegative={false}
                        isAdded={true}
                        isSmall={true}
                        isLowOpacity={true}
                      />
                      <RenderMetric
                        className="pl-16"
                        label="Buyer Shipping Fees"
                        value={current?.totalShippingSalePrice}
                        tooltip="Shipping fees paid by the buyer. Deducted from the customer payment as it does not count towards the seller's revenue."
                        isNegative={true}
                        isSmall={true}
                        isLowOpacity={true}
                      />
                      <RenderMetric
                        className="pl-16"
                        label="Reimbursements"
                        value={current?.totalSellerShippingSalePrice}
                        tooltip="Shipping fees forwarded to you, from the buyer, via TikTok. Only present when shipping has been paid by the buyer, and you, the seller, are fulfilling the order."
                        isAdded={true}
                        isLowOpacity={true}
                        isSmall={true}
                      />
                    </>
                  }
                />
                <RenderMetric
                  className="pl-8"
                  label="Platform Discounts"
                  value={current?.totalPlatformDiscounts}
                  tooltip="Platform discounts applied to the customer payment. Excludes cancelled platform discounts."
                  isNegative={false}
                  isAdded={true}
                  isSmall={true}
                  isLowOpacity={true}
                />
              </>
            }
          />
          <RenderMetric
            label="Net Sales"
            subtitle="excl. cancelled orders"
            value={current?.totalMerchandiseSales}
            tooltip="Net revenue amount after seller discounts are deducted. This figure does not include shipping fees or cancelled orders."
            children={
              <>
                <RenderMetric
                  className="pl-8"
                  label="Net Payment"
                  subtitle="minus buyer shipping fees"
                  value={
                    current?.totalCustomerPayment -
                    current?.totalCancelledCustomerPayment -
                    (current?.totalShippingSalePrice -
                      current?.totalCancelledShippingSalePrice) + (current?.totalSellerShippingSalePrice ?? 0)
                  }
                  tooltip="Customer payment after deducting shipping fees paid by the buyer. Excludes cancelled payments."
                  isAdded={true}
                  isSmall={true}
                  isLowOpacity={true}
                  children={
                    <>
                      <RenderMetric
                        className="pl-16"
                        label="Payment"
                        value={
                          Math.abs(current?.totalCustomerPayment ?? 0) -
                          Math.abs(current?.totalCancelledCustomerPayment ?? 0)
                        }
                        tooltip="Total payments received from customers. Excludes cancelled payments."
                        isNegative={false}
                        isAdded={true}
                        isSmall={true}
                        isLowOpacity={true}
                        children={
                          <>
                            <RenderMetric
                              className="pl-20"
                              label="Total"
                              value={current?.totalCustomerPayment}
                              tooltip="Total payments received from customers. Includes cancelled payments."
                              isAdded={true}
                              isSmall={true}
                              isLowOpacity={true}
                            />
                            <RenderMetric
                              className="pl-20"
                              label="Cancelled"
                              value={current?.totalCancelledCustomerPayment}
                              tooltip="Total cancelled payments received from customers."
                              isNegative={true}
                              isAdded={true}
                              isSmall={true}
                              isLowOpacity={true}
                            />
                          </>
                        }
                      />
                      <RenderMetric
                        className="pl-16"
                        label="Buyer-paid Shipping Fees"
                        value={
                          current?.totalShippingSalePrice -
                          current?.totalCancelledShippingSalePrice
                        }
                        tooltip="Shipping fees paid by the buyer. Only deducted from net sales amount when the order is fulfilled by TikTok or shipped by TikTok.."
                        isNegative={true}
                        isSmall={true}
                        isLowOpacity={true}
                      />
                      <RenderMetric
                        className="pl-16"
                        label="Reimbursements"
                        value={current?.totalSellerShippingSalePrice}
                        tooltip="Shipping fees forwarded to you, from the buyer, via TikTok. Only present when shipping has been paid by the buyer, and you, the seller, are fulfilling the order."
                        isAdded={true}
                        isSmall={true}
                        isLowOpacity={true}
                      />
                    </>
                  }
                />
                <RenderMetric
                  className="pl-8"
                  label="Net Platform Discounts"
                  value={current?.totalNetPlatformDiscounts}
                  tooltip="Platform discounts applied to the customer payment. Excludes cancelled platform discounts."
                  isNegative={false}
                  isAdded={true}
                  isSmall={true}
                  isLowOpacity={true}
                  children={
                    <>
                      <RenderMetric
                        className="pl-16"
                        label="Total"
                        value={current?.totalPlatformDiscounts}
                        tooltip="Total platform discounts applied to the customer payment. Includes cancelled platform discounts."
                        isAdded={true}
                        isSmall={true}
                        isLowOpacity={true}
                      />
                      <RenderMetric
                        className="pl-16"
                        label="Cancelled"
                        value={current?.totalCancelledPlatformDiscounts}
                        tooltip="Total cancelled platform discounts applied to the customer payment."
                        isNegative={true}
                        isAdded={true}
                        isSmall={true}
                        isLowOpacity={true}
                      />
                    </>
                  }
                />
              </>
            }
          />
          <RenderMetric
            label="Cost of Goods"
            value={current?.costOfGoods}
            tooltip="Cost of units sold. May or may not exclude cancelled orders based on your settings. Click configure to change."
            isNegative={true}
            extra={<CogsCalcPopover type="CONFIGURE" />}
          />
          {adSpendEnabled && (
            <RenderMetric
              label="Advertising Costs"
              value={(current?.totalSpend ?? 0) + (current?.advertisingExpenses ?? 0)}
              tooltip="Total advertising costs including ad spend and advertising expenses."
              isNegative={true}
              overrideRender={
                adSpendSyncStatus === "PENDING" ? (
                  <div className="text-orange-500 flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" /> Syncing
                  </div>
                ) : undefined
              }
            >
              <RenderMetric
                className="pl-8"
                label="Ad Spend"
                value={current?.totalSpend}
                tooltip="Total spend on ads (regular Ads and GMV Max Ads). Inclusive of VAT."
                isNegative={true}
                isSmall={true}
                isLowOpacity={true}
              />
              <RenderMetric
                className="pl-8"
                label="Advertising Expenses"
                value={current?.advertisingExpenses}
                tooltip="Additional advertising expenses and costs."
                isNegative={true}
                isSmall={true}
                isLowOpacity={true}
              />
            </RenderMetric>
          )}
          <RenderMetric
            label="TikTok Commiss. Fee"
            value={current?.totalPlatformFee}
            tooltip="Estimation of fees charged by the platform for using their services."
            isNegative={true}
            extra={<PlatformFeesConfigureButton type="CONFIGURE" />}
          />
          <RenderMetric
            label="Affiliates"
            value={current?.totalEstimatedAffiliateCommission}
            tooltip="Estimated fees paid to affiliates for referring customers (incl. ads and partner commissions)."
            isNegative={true}
          />
          <RenderMetric
            label="Shipping Costs"
            value={
              (current?.totalEstimatedFbmShippingCost ?? 0) +
              (current?.totalEstimatedSellerShippingCost ?? 0) +
              (current?.totalEstimatedFbtShippingCost ?? 0) +
              (current?.shippingExpenses ?? 0)
            }
            tooltip="Total shipping costs across all fulfillment methods."
            isNegative={true}
          >
            <RenderMetric
              className="pl-8"
              label="FBM Shipping"
              value={current?.totalEstimatedFbmShippingCost}
              tooltip="Shipping fee's paid for orders auto-shipped with TikTok, but fulfilled by the seller (Fulfilled by Merchant)."
              isNegative={true}
              isSmall={true}
              isLowOpacity={true}
            />
            <RenderMetric
              className="pl-8"
              label="Seller-Fulfilled Shipping"
              value={current?.totalEstimatedSellerShippingCost}
              tooltip="Shipping fee's paid for orders managed, and fulfilled by the seller (you)."
              isNegative={true}
              isSmall={true}
              isLowOpacity={true}
            />
            <RenderMetric
              className="pl-8"
              label="FBT Shipping"
              value={current?.totalEstimatedFbtShippingCost}
              tooltip="Shipping fee's for orders fulfilled using Fulfilled by TikTok (FBT)."
              isNegative={true}
              isSmall={true}
              isLowOpacity={true}
            />
            <RenderMetric
              className="pl-8"
              label="Shipping Expenses"
              value={current?.shippingExpenses}
              tooltip="Shipping expenses paid for orders."
              isNegative={true}
              isSmall={true}
              isLowOpacity={true}
            />
          </RenderMetric>
          <RenderMetric
            label="Other Expenses"
            value={current?.otherExpenses}
            tooltip="Other miscellaneous expenses and costs."
            isNegative={true}
          />
          <RenderMetric
            label="Fulfillment & Packaging"
            value={
              (current?.totalEstimatedFbtFulfilmentAndPackagingFees ?? 0) +
              (current?.fulfilmentExpenses ?? 0)
            }
            tooltip="Total fulfillment and packaging costs across all methods."
            isNegative={true}
          >
            <RenderMetric
              className="pl-8"
              label="FBT"
              value={current?.totalEstimatedFbtFulfilmentAndPackagingFees}
              tooltip="Fee's paid to TikTok for the fulfillment of orders using Fulfilled by TikTok (FBT). Estimated based on product size."
              isNegative={true}
              isSmall={true}
              isLowOpacity={true}
              extra={<FbtFulfillmentFeesConfigureButton type="CONFIGURE" />}
            />
            <RenderMetric
              className="pl-8"
              label="Expenses"
              value={current?.fulfilmentExpenses}
              tooltip="Additional fulfillment expenses and costs entered by the user."
              isNegative={true}
              isSmall={true}
              isLowOpacity={true}
            />
          </RenderMetric>
          <RenderMetric
            label="Net Tax"
            value={Math.abs(
              -Math.abs(current?.totalTax) + Math.abs(current?.totalTaxBack)
            )}
            tooltip="Sales VAT minus tax back on costs."
            isNegative={
              (-Math.abs(current?.totalTax) + Math.abs(current?.totalTaxBack)) < 0
            }
            isAdded={
              (-Math.abs(current?.totalTax) + Math.abs(current?.totalTaxBack)) > 0
            }
            extra={<TaxSettingsPopover type="CONFIGURE" />}
          >
            <RenderMetric
              className="pl-8"
              label="Tax"
              value={current?.totalTax}
              tooltip="Sales VAT on sales."
              isNegative={true}
              isSmall={true}
              isLowOpacity={true}
            />
            <RenderMetric
              className="pl-8"
              label="Tax Refunds"
              value={current?.totalTaxBack}
              tooltip="Tax refunds on costs."
              isAdded={true}
              isSmall={true}
              isLowOpacity={true}
              extra={<TaxSettingsPopover type="CONFIGURE" />}
            />
          </RenderMetric>
        </div>
      </div>
      <div className="flex flex-col">
        <RenderMetric
          className="border-none bg-muted/20"
          label="Net Profits"
          value={current?.netProfits}
          tooltip="Earnings after all costs and expenses"
          isNegative={current?.netProfits < 0}
          isAdded={current?.netProfits > 0}
        />
        <RenderMetric
          className="border-none bg-muted/20"
          label="Net Margin"
          value={((current?.netProfits ?? 0) / ((current?.totalMerchandiseSales ?? 0))) * 100}
          tooltip="Net margin as a percentage of net sales. Formula: (Net Profits / Net Sales) * 100"
          isPercentage={true}
        />
        <RenderMetric
          className="border-none bg-muted/20"
          label="COGS ROI"
          value={
            current?.netProfits && current?.costOfGoods
              ? (current.netProfits / current.costOfGoods) * 100
              : 0
          }
          tooltip="Return on Investment - Net profits as a percentage of cost of goods."
          isPercentage={true}
        />
        {adSpendEnabled && (
          <RenderMetric
            className="border-none bg-muted/20"
            label="Ad Spend ROI"
            value={
              current?.netProfits && current?.totalSpend
                ? (current.netProfits / current.totalSpend) * 100
                : 0
            }
            tooltip="Return on Ad Spend - Net profits as a percentage of ad spend."
            isPercentage={true}
          />
        )}
        <RenderMetric
          className="border-none bg-muted/20"
          label="Affiliate Spend ROI"
          value={
            current?.netProfits && current?.totalEstimatedAffiliateCommission
              ? (current.netProfits /
                  current.totalEstimatedAffiliateCommission) *
                100
              : 0
          }
          tooltip="Return on Affiliate Spend - Net profits as a percentage of affiliate commission costs."
          isPercentage={true}
        />
      </div>
    </Collapsible>
  );
};

export default function PandLOverviewTile() {
  const { data: organisation } = useOrganisationPlan();
  const adSpendEnabled = organisation?.adSpendEnabled;
  const navigate = useNavigate();
  const [dateRangeCustomSelectContext, setDateRangeCustomSelectContext] =
    useState<1 | 2>(1);
  const [openCustomDateRange, setOpenCustomDateRange] = useState(false);
  const { currency, salesAndProfit } = useContext(UserStateContext);
  const {
    dateRangeGroup1,
    dateRangeGroup2,
    setDateRangeGroup1,
    setDateRangeGroup2,
    customDateRangeGroup1,
    setCustomDateRangeGroup1,
    customDateRangeGroup2,
    setCustomDateRangeGroup2,
  } = salesAndProfit;
  const [open, setOpen] = useState<string | undefined>(undefined);
  const { data, isLoading } = useMultipleLiteDashboardStatistics(
    [
      dateRangeGroup1.current,
      dateRangeGroup1.previous,
      dateRangeGroup2.current,
      dateRangeGroup2.previous,
    ],
    undefined,
    {
      dateRange1: {
        current: customDateRangeGroup1.current,
        previous: customDateRangeGroup1.previous,
      },
      dateRange2: {
        current: customDateRangeGroup2.current,
        previous: customDateRangeGroup2.previous,
      },
    }
  );

  const dateRangeOptions: {
    value: DateRangeGroup;
    label: string;
  }[] = [
    { value: "today", label: "Today" },
    { value: "yesterday", label: "Yesterday" },
    { value: "this_week", label: "This Week" },
    { value: "last_week", label: "Last Week" },
    { value: "this_month", label: "This Month" },
    { value: "last_month", label: "Last Month" },
    { value: "this_year", label: "This Year" },
    { value: "last_year", label: "Last Year" },
    { value: "this_quarter", label: "This Quarter" },
    { value: "last_quarter", label: "Last Quarter" },
    { value: "custom_a_1", label: "Custom" },
    { value: "custom_a_2", label: "Custom" },
    { value: "custom_b_1", label: "Custom" },
    { value: "custom_b_2", label: "Custom" },
  ];

  const dateRangeComparisonOptions: {
    current: DateRangeGroup;
    previous: DateRangeGroup;
    label: string;
  }[] = [
    { current: "today", previous: "yesterday", label: "Today vs Yesterday" },
    {
      current: "this_week",
      previous: "last_week",
      label: "This Week vs Last Week",
    },
    {
      current: "this_month",
      previous: "last_month",
      label: "This Month vs Last Month",
    },
    {
      current: "this_quarter",
      previous: "last_quarter",
      label: "This Quarter vs Last Quarter",
    },
    {
      current: "this_year",
      previous: "last_year",
      label: "This Year vs Last Year",
    },
  ];

  const getIconForDateRange = (range: string) => {
    switch (range) {
      case "today":
        return (
          <div className="h-8 w-8 rounded-full bg-green-400 flex items-center justify-center">
            <DollarSign className="shrink-0 h-4 w-4 text-green-800" />
          </div>
        );
      case "yesterday":
        return (
          <div className="h-8 w-8 rounded-full bg-blue-500 flex items-center justify-center">
            <DollarSign className="shrink-0 h-4 w-4 text-blue-800" />
          </div>
        );
      case "this_week":
      case "this_month":
      case "this_year":
        return (
          <div className="h-8 w-8 rounded-full bg-purple-500 flex items-center justify-center">
            <DollarSign className="shrink-0 h-4 w-4 text-purple-800" />
          </div>
        );
      case "custom_a_1":
        return (
          <div className="h-8 w-8 rounded-full bg-green-400 flex items-center justify-center">
            <DollarSign className="shrink-0 h-4 w-4 text-green-800" />
          </div>
        );
      case "custom_a_2":
        return (
          <div className="h-8 w-8 rounded-full bg-blue-500 flex items-center justify-center">
            <DollarSign className="shrink-0 h-4 w-4 text-blue-800" />
          </div>
        );
      case "custom_b_1":
        return (
          <div className="h-8 w-8 rounded-full bg-green-400 flex items-center justify-center">
            <DollarSign className="shrink-0 h-4 w-4 text-green-800" />
          </div>
        );
      case "custom_b_2":
        return (
          <div className="h-8 w-8 rounded-full bg-blue-500 flex items-center justify-center">
            <DollarSign className="shrink-0 h-4 w-4 text-blue-800" />
          </div>
        );
      default:
        return (
          <div className="h-8 w-8 rounded-full bg-orange-500 flex items-center justify-center">
            <DollarSign className="shrink-0 h-4 w-4 text-orange-800" />
          </div>
        );
    }
  };

  const onDateRangeChange = (
    baseRange: {
      from: Date | undefined;
      to: Date | undefined;
    },
    comparisonRange: {
      from: Date | undefined;
      to: Date | undefined;
    }
  ) => {
    if (dateRangeCustomSelectContext === 1) {
      setCustomDateRangeGroup1({
        current: baseRange,
        previous: comparisonRange,
      });
      setDateRangeGroup1({
        current: "custom_a_1",
        previous: "custom_a_2",
      });
    } else {
      setCustomDateRangeGroup2({
        current: baseRange,
        previous: comparisonRange,
      });
      setDateRangeGroup2({
        current: "custom_b_1",
        previous: "custom_b_2",
      });
    }
  };

  useEffect(() => {
    console.log(customDateRangeGroup1);
  }, [customDateRangeGroup1]);

  return (
    <LoadingFallback loading={isLoading}>
      {openCustomDateRange && (
        <SelectComparisonDateRange
          open={openCustomDateRange}
          setOpen={setOpenCustomDateRange}
          initialBaseFrom={
            dateRangeCustomSelectContext === 1
              ? customDateRangeGroup1.current.from
              : customDateRangeGroup2.current.from
          }
          initialBaseTo={
            dateRangeCustomSelectContext === 1
              ? customDateRangeGroup1.current.to
              : customDateRangeGroup2.current.to
          }
          initialComparisonFrom={
            dateRangeCustomSelectContext === 1
              ? customDateRangeGroup1.previous.from
              : customDateRangeGroup2.previous.from
          }
          initialComparisonTo={
            dateRangeCustomSelectContext === 1
              ? customDateRangeGroup1.previous.to
              : customDateRangeGroup2.previous.to
          }
          onDateRangeChange={(baseRange, comparisonRange) => {
            onDateRangeChange(baseRange, comparisonRange);
            setOpenCustomDateRange(false);
          }}
        />
      )}
      <Accordion type="single" collapsible value={open} onValueChange={setOpen}>
        <AccordionItem value="pandl-overview">
          <AccordionTrigger
            className={cn(
              "px-6 hover:no-underline hover:bg-transparent group",
              open === "pandl-overview"
                ? "opacity-100"
                : "opacity-50 hover:opacity-100"
            )}
          >
            <div className="flex items-center gap-2">
              <h3 className="text-lg font-semibold text-foreground">
                P&L Overview
              </h3>
              <span className="text-sm text-muted-foreground group-hover:text-foreground">
                Click to expand
              </span>
            </div>
          </AccordionTrigger>
          <AccordionContent>
            <div className="p-6 pt-0 grid lg:grid-cols-2 grid-cols-1 items-stretch">
              <div className="space-y-2 p-2 border rounded-l-md pr-2">
                <Select
                  value={`${dateRangeGroup1.current}-${dateRangeGroup1.previous}`}
                  onValueChange={(value) => {
                    const [current, previous] = value.split("-");
                    setDateRangeGroup1({
                      current: current as DateRangeGroup,
                      previous: previous as DateRangeGroup,
                    });
                  }}
                >
                  <SelectTrigger className="lg:w-[60%] w-full">
                    <div className="flex w-full justify-between items-center">
                      <div className="flex items-center gap-2">
                        <div className="text-sm font-medium">Current:</div>
                        <div className="text-muted-foreground">
                          {dateRangeOptions.find(
                              (opt) => opt.value === dateRangeGroup1.current
                            )?.label}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="text-sm font-medium">Previous:</div>
                        <div className="text-muted-foreground">
                          {dateRangeOptions.find(
                            (opt) => opt.value === dateRangeGroup1.previous
                          )?.label}
                        </div>
                      </div>
                    </div>
                  </SelectTrigger>
                  <SelectContent>
                    {dateRangeComparisonOptions.map((option) => (
                      <SelectItem
                        key={`${option.current}-${option.previous}`}
                        value={`${option.current}-${option.previous}`}
                      >
                        {option.label}
                      </SelectItem>
                    ))}
                    <Button
                      variant="ghost"
                      className="w-full justify-start pl-8 relative m-0 font-medium text-sm"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setDateRangeCustomSelectContext(1);
                        setOpenCustomDateRange(true);
                      }}
                    >
                      <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
                        {dateRangeGroup1.current === "custom_a_1" && (
                          <Check className="h-4 w-4" />
                        )}
                      </span>
                      Custom
                    </Button>
                  </SelectContent>
                </Select>

                <div className="grid md:grid-cols-2 grid-cols-1 gap-4">
                  <RenderTile
                    customDateRange={customDateRangeGroup1.current}
                    current={
                      data?.[dateRangeGroup1.current as keyof typeof data]
                    }
                    previous={
                      data?.[dateRangeGroup1.previous as keyof typeof data]
                    }
                    dateRange={dateRangeGroup1.current}
                    showComparison={true}
                    dateRangeOptions={dateRangeOptions}
                    getIconForDateRange={getIconForDateRange}
                    adSpendEnabled={adSpendEnabled ?? false}
                    adSpendSyncStatus={organisation?.adSyncStatus}
                  />
                  <RenderTile
                    customDateRange={customDateRangeGroup1.previous}
                    current={
                      data?.[dateRangeGroup1.previous as keyof typeof data]
                    }
                    previous={null}
                    dateRange={dateRangeGroup1.previous}
                    showComparison={false}
                    dateRangeOptions={dateRangeOptions}
                    getIconForDateRange={getIconForDateRange}
                    adSpendEnabled={adSpendEnabled ?? false}
                    adSpendSyncStatus={organisation?.adSyncStatus}
                  />
                </div>
              </div>

              <div className="pl-2 border-t border-b border-r p-2 rounded-r-md space-y-2">
                <Select
                  value={`${dateRangeGroup2.current}-${dateRangeGroup2.previous}`}
                  onValueChange={(value) => {
                    if (value === "custom") {
                      setDateRangeCustomSelectContext(2);
                      setOpenCustomDateRange(true);
                      setDateRangeGroup2({
                        current: "custom_b_1",
                        previous: "custom_b_2",
                      });
                      return;
                    }
                    const [current, previous] = value.split("-");
                    setDateRangeGroup2({
                      current: current as DateRangeGroup,
                      previous: previous as DateRangeGroup,
                    });
                  }}
                >
                  <SelectTrigger className="lg:w-[60%] w-full">
                    <div className="flex w-full justify-between items-center">
                      <div className="flex items-center gap-2">
                        <div className="text-sm font-medium">Current:</div>
                        <div className="text-muted-foreground">
                          {dateRangeOptions.find(
                              (opt) => opt.value === dateRangeGroup2.current
                            )?.label}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="text-sm font-medium">Previous:</div>
                        <div className="text-muted-foreground">
                          {dateRangeOptions.find(
                              (opt) => opt.value === dateRangeGroup2.previous
                            )?.label}
                        </div>
                      </div>
                    </div>
                  </SelectTrigger>
                  <SelectContent>
                    {dateRangeComparisonOptions.map((option) => (
                      <SelectItem
                        key={`${option.current}-${option.previous}`}
                        value={`${option.current}-${option.previous}`}
                      >
                        {option.label}
                      </SelectItem>
                    ))}
                    <Button
                      variant="ghost"
                      className="w-full justify-start pl-8 relative m-0 font-medium text-sm"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setDateRangeCustomSelectContext(2);
                        setOpenCustomDateRange(true);
                      }}
                    >
                      <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
                        {dateRangeGroup2.current === "custom_b_1" && (
                          <Check className="h-4 w-4" />
                        )}
                      </span>
                      Custom
                    </Button>
                  </SelectContent>
                </Select>

                <div className="grid md:grid-cols-2 grid-cols-1 gap-4">
                  <RenderTile
                    customDateRange={customDateRangeGroup2.current}
                    current={
                      data?.[dateRangeGroup2.current as keyof typeof data]
                    }
                    previous={
                      data?.[dateRangeGroup2.previous as keyof typeof data]
                    }
                    dateRange={dateRangeGroup2.current}
                    showComparison={true}
                    dateRangeOptions={dateRangeOptions}
                    getIconForDateRange={getIconForDateRange}
                    adSpendEnabled={adSpendEnabled ?? false}
                    adSpendSyncStatus={organisation?.adSyncStatus}
                  />
                  <RenderTile
                    customDateRange={customDateRangeGroup2.previous}
                    current={
                      data?.[dateRangeGroup2.previous as keyof typeof data]
                    }
                    previous={null}
                    dateRange={dateRangeGroup2.previous}
                    showComparison={false}
                    dateRangeOptions={dateRangeOptions}
                    getIconForDateRange={getIconForDateRange}
                    adSpendEnabled={adSpendEnabled ?? false}
                    adSpendSyncStatus={organisation?.adSyncStatus}
                  />
                </div>
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </LoadingFallback>
  );
}
